/* eslint-disable react/forbid-elements */
import React, { useEffect } from 'react';
import { SurveyStates } from '../../../../../common/src/enums/SurveyStates';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetch } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    EditPanelCell,
    EditPanelFilterSelect,
    EditPanelRow,
    EditPanelTextArea,
    ISelectOption,
    PanelBody,
} from '../../elements';
import { showSavedToast } from '../../utils/toastMessages';

type SurveyEditWorkflowAssignmentBodyState = {
    text: string;
    assignedToUserId: string | null;
    status: SurveyStates | null;
};

export function SurveyEditWorkflowAssignmentBody(props: { surveyId: string }) {
    const { surveyId } = props;
    const translate = useTranslate();
    const [state, setState] = React.useState<SurveyEditWorkflowAssignmentBodyState>({ assignedToUserId: null, status: null, text: '' });
    const [usersToSelect, setUsersToSelect] = React.useState<ISelectOption[]>([]);

    const { data: currentSurvey, reload: reloadSurvey } = useOpenApiFetch('/api/surveys/{surveyId}', 'get', { surveyId }, []);
    const { data: users } = useOpenApiFetch('/api/surveys/{surveyId}/assignment/allowedUsers', 'get', { surveyId }, []);
    const { data: allowedStates } = useOpenApiFetch('/api/surveys/{surveyId}/assignment/allowedStates', 'get', { surveyId }, []);

    const [isDataChanged, setIsDataChanged] = React.useState(false);

    useEffect(() => {
        if (users) {
            const options = users.map((user) => ({ value: user.id, text: `${user.firstName} ${user.lastName}`, user }));
            const nullOption = { value: 'null', text: translate('workflow.unassigned') };
            setUsersToSelect([nullOption, ...options]);
        }
    }, [users, translate]);

    useEffect(() => {
        if (currentSurvey) {
            setState((i) => ({ ...i, assignedToUserId: currentSurvey.assignedToUserId, status: currentSurvey.status as SurveyStates }));
        }
    }, [currentSurvey]);

    useEffect(() => {
        if (currentSurvey) {
            if (state.assignedToUserId !== currentSurvey?.assignedToUserId || state.status !== currentSurvey?.status) {
                setIsDataChanged(true);
            } else {
                setIsDataChanged(false);
            }
        }
    }, [state, currentSurvey]);

    async function save() {
        if (state.status) {
            await openApiFetch(
                '/api/surveys/{surveyId}/assignment',
                'post',
                { surveyId },
                { assignedToUserId: state.assignedToUserId, status: state.status, comment: state.text },
            );
            setState((i) => ({ ...i, text: '' }));
            showSavedToast(translate);
            await reloadSurvey();
        }
    }

    return (
        <PanelBody isForm={true}>
            <EditPanelRow>
                <EditPanelCell rowCount={6}>
                    <EditPanelFilterSelect
                        value={state.assignedToUserId}
                        handleChange={(value) => {
                            const newValue = value === 'null' ? null : value;
                            setState((i) => ({ ...i, assignedToUserId: newValue }));
                        }}
                        name="assignedToUserId"
                        topLabel={translate('workflow.assignedTo')}
                        options={usersToSelect}
                    />
                </EditPanelCell>
                <EditPanelCell rowCount={6}>
                    <EditPanelFilterSelect
                        options={allowedStates?.map((i) => ({ value: i, text: translate(`surveyStatus.${i}`) })) ?? []}
                        value={state.status}
                        handleChange={(value) => {
                            setState((i) => ({ ...i, status: value as SurveyStates }));
                        }}
                        name="status"
                        topLabel={translate('common.status')}
                    />
                </EditPanelCell>
            </EditPanelRow>
            {isDataChanged && (
                <EditPanelRow>
                    <EditPanelCell rowCount={9}>
                        <EditPanelTextArea
                            value={state.text}
                            handleChange={(value) => {
                                setState((i) => ({ ...i, text: value }));
                            }}
                            name="text"
                            icon="fa fa-comments"
                            placeholder={translate('workflow.comment')}
                        />
                    </EditPanelCell>
                    <EditPanelCell rowCount={3}>
                        <EditPanelButton primary text={translate('common.save')} onClick={save} iconClass="fa fa-save" small />
                    </EditPanelCell>
                </EditPanelRow>
            )}
        </PanelBody>
    );
}

export function SurveyEditWorkflowAssignment(props: { surveyId: string }) {
    const { surveyId } = props;
    const translate = useTranslate();

    return (
        <EditPanel bottomMargin={true} noPadding={true}>
            <CollapsiblePanel heading={{ dark: true, text: translate('workflow.assignment'), initialIsClosed: false }}>
                <SurveyEditWorkflowAssignmentBody surveyId={surveyId} />
            </CollapsiblePanel>
        </EditPanel>
    );
}
